import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Settings2 } from "lucide-react";

export interface Column {
  id: string;
  label: string;
  visible: boolean;
  locked?: boolean; // Cannot be hidden (e.g., Actions column)
}

interface ColumnSettingsProps {
  columns: Column[];
  onColumnsChange: (columns: Column[]) => void;
}

export function ColumnSettings({ columns, onColumnsChange }: ColumnSettingsProps) {
  // Toggle individual column visibility
  const toggleColumn = (columnId: string) => {
    const updated = columns.map(col => 
      col.id === columnId && !col.locked 
        ? { ...col, visible: !col.visible } 
        : col
    );
    onColumnsChange(updated);
  };

  // Show all columns
  const showAll = () => {
    const updated = columns.map(col => ({ ...col, visible: true }));
    onColumnsChange(updated);
  };

  // Hide all non-locked columns
  const hideAll = () => {
    const updated = columns.map(col => 
      col.locked ? col : { ...col, visible: false }
    );
    onColumnsChange(updated);
  };

  const visibleCount = columns.filter(c => c.visible).length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings2 className="h-4 w-4 mr-2" />
          Columns ({visibleCount}/{columns.length})
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-64 bg-background z-50"
      >
        <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Quick Actions */}
        <div className="flex gap-2 px-2 py-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={showAll}
            className="flex-1 h-8 text-xs"
          >
            Show All
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={hideAll}
            className="flex-1 h-8 text-xs"
          >
            Hide All
          </Button>
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Column Checkboxes */}
        <div className="max-h-80 overflow-y-auto">
          {columns.map((column) => (
            <DropdownMenuCheckboxItem
              key={column.id}
              checked={column.visible}
              onCheckedChange={() => toggleColumn(column.id)}
              disabled={column.locked}
            >
              {column.label}
              {column.locked && (
                <span className="ml-2 text-xs text-muted-foreground">(locked)</span>
              )}
            </DropdownMenuCheckboxItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
